import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import {
  apiErrorText,
  apiErrorStatus,
  blobErrorText,
  useSetProjectContractMutation,
  useGenerateKsDraftMutation,
  useListKsDraftsQuery,
  useGetKsDraftQuery,
  useLazyExportKsDraftXlsxQuery,
  useSetKsItemMatchMutation,
  useResetKsItemMatchMutation,
  useListKsEstimateItemsQuery,
  useGetPtoInfoQuery,
  useGetPtoProjectContractQuery,
  useListPtoContractOptionsQuery,
  usePushKsDraftToSmetaMutation,
  useCreateRequestMutation,
  type FieldKsDraft,
  type FieldKsDraftItem,
  type KsDraftSuggestion,
  type KsDraftSummary,
  type KsEstimateItemOption,
  type PtoContractBrief,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { Combobox, type ComboboxOption } from '@shared/ui/Combobox';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 380px) 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const Small = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Strong = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Check = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  margin: ${({ theme }) => theme.spacing(2)} 0;
`;

const Filters = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const DraftItem = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  font: inherit;
  color: inherit;
`;

const TableWrap = styled.div`
  overflow: auto;
  max-height: 62vh;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;

  th,
  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: 6px 8px;
    text-align: left;
    white-space: nowrap;
  }
  th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    font-weight: 600;
  }
  td.num {
    text-align: right;
  }
  td.wrap {
    white-space: normal;
    min-width: 220px;
  }
  tr[data-over='true'] td {
    background: rgba(220, 50, 50, 0.1);
  }
  tr[data-selected='true'] td {
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const Banner = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 14px;
`;

const Totals = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
`;

const Over = styled.span`
  color: ${({ theme }) => theme.colors.status.danger};
  font-weight: 600;
`;

const ErrorNote = styled.div.attrs({ role: 'alert' })`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const MATCH_LABEL: Record<string, string> = {
  unique: 'сматчено',
  confirmed: 'подтверждено ПТО',
  ambiguous: 'неоднозначно',
  no_match: 'нет в смете',
  declined: 'нет в смете (решение ПТО)',
  // Позиция сметы только предложена — в стоимость не входит до решения ПТО.
  candidate: 'кандидат — проверьте',
  unit_mismatch: 'единицы не совпадают',
};

/** Статусы, по которым ждут решения инженера. */
const NEEDS_DECISION = new Set(['candidate', 'unit_mismatch', 'ambiguous']);

const num = (v: number | null | undefined) =>
  v === null || v === undefined ? '' : v.toLocaleString('ru-RU', { maximumFractionDigits: 3 });

const rub = (v: number) => `${v.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;

function isDisabled(err: unknown): boolean {
  return (err as { status?: number } | undefined)?.status === 404;
}

function draftLabel(d: FieldKsDraft): string {
  const total = d.total_cost ? ` · ${rub(d.total_cost)}` : '';
  return `${d.period_from} — ${d.period_to}${total}`;
}

function contractLabel(c: PtoContractBrief): string {
  const no = c.contract_number ? `№ ${c.contract_number}` : 'без номера';
  return `${no} — ${c.counterparty}`;
}

function contractSublabel(c: PtoContractBrief): string {
  const parts: string[] = [];
  if (c.subject) parts.push(c.subject);
  parts.push(
    c.active_estimate_id
      ? `смета, версия ${c.active_estimate_version ?? '?'}, позиций ${c.active_estimate_items ?? 0}`
      : 'нет активной сметы — КС-2 не сформировать',
  );
  if (c.archived) parts.push('в архиве');
  return parts.join(' · ');
}

function estimateLabel(s: KsDraftSuggestion | KsEstimateItemOption): string {
  const pos = s.position_no !== null ? `поз. ${s.position_no}` : 'поз. —';
  return `${pos} · ${s.code || 'без шифра'} · ${s.name}`;
}

function estimateSublabel(s: KsDraftSuggestion | KsEstimateItemOption): string {
  const price = s.unit_price !== null ? `${num(s.unit_price)} ₽ за ед.` : 'цены нет';
  const qty =
    s.contract_qty !== null ? `по договору ${num(s.contract_qty)}` : 'договорного объёма нет';
  return `${s.unit ?? 'ед. не указана'} · ${price} · ${qty}`;
}

/** Сводка по строкам, если сервер её не прислал (старый ответ из кэша). */
function localSummary(items: FieldKsDraftItem[]): KsDraftSummary {
  const byWork = new Map<string, string>();
  for (const it of items) {
    const key = it.fact_id ?? it.id;
    if (!byWork.has(key)) byWork.set(key, it.match_status);
  }
  const count = (s: string) => [...byWork.values()].filter((v) => v === s).length;
  const over = items.filter((it) => it.over_contract);
  return {
    lines: items.length,
    works: byWork.size,
    unique: count('unique'),
    confirmed: count('confirmed'),
    candidate: count('candidate'),
    unit_mismatch: count('unit_mismatch'),
    ambiguous: count('ambiguous'),
    no_match: count('no_match'),
    declined: count('declined'),
    priced: count('unique') + count('confirmed'),
    need_decision: count('candidate') + count('unit_mismatch') + count('ambiguous'),
    total_cost: items.reduce((s, it) => s + (it.cost ?? 0), 0),
    over_contract_lines: over.length,
    over_contract_cost: over.reduce((s, it) => s + (it.qty ?? 0) * (it.unit_price ?? 0), 0),
  };
}

/** Договор проекта: показ реквизитов и выбор из списка доступных (вместо UUID).
 *  Выбирает и привязывает тот, кто пишет в проект и видит договоры (Статут) или
 *  сметы (Сметчик), — как проверяет сервер; остальным кнопка не предлагается. */
function ContractPanel({
  projectId,
  canWrite,
  disabled,
  onNotice,
}: {
  projectId: string;
  canWrite: boolean;
  disabled: boolean;
  onNotice: (text: string) => void;
}) {
  const statut = useAgentPermission('statut');
  const smeta = useAgentPermission('smeta');
  const canPick = canWrite && (statut.can_read || smeta.can_read);
  const { data } = useGetPtoProjectContractQuery(projectId, { skip: !projectId || disabled });
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<PtoContractBrief | null>(null);
  const optionsQ = useListPtoContractOptionsQuery({ projectId, q: query }, { skip: !editing });
  const [setContract, { isLoading: saving }] = useSetProjectContractMutation();
  const onQueryChange = useCallback((q: string) => setQuery(q), []);

  const items = optionsQ.data?.items ?? [];
  const options: ComboboxOption[] = items.map((c) => ({
    id: c.id,
    label: contractLabel(c),
    sublabel: contractSublabel(c),
  }));
  const forbidden = apiErrorStatus(optionsQ.error) === 403;
  const current = data?.contract ?? null;

  async function save(contractId: string | null) {
    try {
      await setContract({ projectId, contract_id: contractId }).unwrap();
      onNotice(contractId ? 'Договор привязан.' : 'Договор отвязан.');
      setEditing(false);
      setChosen(null);
    } catch (e) {
      onNotice(
        apiErrorStatus(e) === 403
          ? 'Привязку договора делает сотрудник с доступом к договорам (Статут) или сметам (Сметчик).'
          : apiErrorText(e, 'Не удалось привязать договор.'),
      );
    }
  }

  return (
    <Panel>
      <h3 style={{ marginTop: 0 }}>Договор</h3>
      {current ? (
        <>
          <Strong>{contractLabel(current)}</Strong>
          <Small>{contractSublabel(current)}</Small>
        </>
      ) : data?.contract_id ? (
        <Muted>Привязанный договор удалён — выберите другой.</Muted>
      ) : (
        <Muted>Проект не привязан к договору: КС-2 сверяется с активной сметой договора.</Muted>
      )}

      {editing ? (
        <>
          <Field style={{ marginTop: 12 }}>
            Договор (номер, контрагент или предмет)
            <Combobox
              id={`ks-contract-${projectId}`}
              value={chosen?.id ?? ''}
              selectedLabel={chosen ? contractLabel(chosen) : undefined}
              options={options}
              onChange={(o) => setChosen(o ? (items.find((c) => c.id === o.id) ?? null) : null)}
              onQueryChange={onQueryChange}
              placeholder="Начните вводить номер договора…"
              loading={optionsQ.isFetching}
              emptyText={
                forbidden
                  ? 'Нет доступа к договорам: привязку делает сотрудник с доступом к Статуту или Сметчику'
                  : optionsQ.isError
                    ? apiErrorText(optionsQ.error, 'Список договоров не загрузился')
                    : 'Договор не найден'
              }
            />
          </Field>
          {chosen && <Small>{contractSublabel(chosen)}</Small>}
          <Actions>
            <Button onClick={() => chosen && void save(chosen.id)} disabled={!chosen || saving}>
              {saving ? 'Сохраняю…' : 'Привязать'}
            </Button>
            {current && (
              <Button $variant="secondary" onClick={() => void save(null)} disabled={saving}>
                Отвязать
              </Button>
            )}
            <Button
              $variant="ghost"
              onClick={() => {
                setEditing(false);
                setChosen(null);
              }}
            >
              Отмена
            </Button>
          </Actions>
        </>
      ) : (
        <>
          <Actions>
            <Button
              $variant="secondary"
              onClick={() => setEditing(true)}
              disabled={!canPick || disabled}
            >
              {current || data?.contract_id ? 'Сменить договор…' : 'Выбрать договор…'}
            </Button>
          </Actions>
          {canWrite && !canPick && (
            <Small>
              Договор выбирает сотрудник с доступом к договорам (Статут) или сметам (Сметчик).
            </Small>
          )}
        </>
      )}
    </Panel>
  );
}

/** Решение инженера по строке черновика: подтвердить предложенную позицию,
 *  выбрать другую, «нет в смете» или снять решение. */
function DecisionPanel({
  item,
  draftId,
  suggestion,
  onDone,
  onClose,
}: {
  item: FieldKsDraftItem;
  draftId: string;
  suggestion: KsDraftSuggestion | null;
  onDone: (text: string) => void;
  onClose: () => void;
}) {
  const [forWork, setForWork] = useState(true);
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<KsEstimateItemOption | null>(null);
  const itemsQ = useListKsEstimateItemsQuery({ draftId, q: query });
  const [setMatch, { isLoading: saving }] = useSetKsItemMatchMutation();
  const [resetMatch, { isLoading: resetting }] = useResetKsItemMatchMutation();
  const [error, setError] = useState<string | null>(null);
  const onQueryChange = useCallback((q: string) => setQuery(q), []);

  // Строку без факта (ручная запись ОЖР) можно решить только для всей работы.
  const scope: 'work' | 'line' = forWork || !item.fact_id ? 'work' : 'line';
  const busy = saving || resetting;
  const decided = item.match_status === 'confirmed' || item.match_status === 'declined';
  const options: ComboboxOption[] = (itemsQ.data?.items ?? []).map((it) => ({
    id: it.id,
    label: estimateLabel(it),
    sublabel: estimateSublabel(it),
  }));

  async function decide(estimateItemId: string | null, text: string) {
    setError(null);
    try {
      await setMatch({ itemId: item.id, estimate_item_id: estimateItemId, scope }).unwrap();
      onDone(`${text} Черновик пересобран: стоимость и накопительный контроль пересчитаны.`);
    } catch (e) {
      setError(apiErrorText(e, 'Решение не сохранено.'));
    }
  }

  async function reset() {
    setError(null);
    try {
      await resetMatch({ itemId: item.id, scope }).unwrap();
      onDone('Решение снято: строки снова сопоставляются автоматически.');
    } catch (e) {
      setError(apiErrorText(e, 'Решение не снято.'));
    }
  }

  return (
    <Panel>
      <Row style={{ marginBottom: 8 }}>
        <Strong>Сопоставление строки со сметой</Strong>
        <Button $variant="ghost" $size="sm" onClick={onClose}>
          Закрыть
        </Button>
      </Row>
      <div>
        {item.name} — {num(item.qty)} {item.unit ?? ''}
      </div>
      <Small>Сейчас: {MATCH_LABEL[item.match_status] ?? item.match_status}</Small>

      {suggestion && item.match_status === 'candidate' && (
        <>
          <Small>Предложено: {estimateLabel(suggestion)}</Small>
          <Small>{estimateSublabel(suggestion)}</Small>
          <Actions>
            <Button
              $size="sm"
              disabled={busy}
              onClick={() => void decide(suggestion.id, 'Позиция подтверждена.')}
            >
              Подтвердить предложенную позицию
            </Button>
          </Actions>
        </>
      )}
      {suggestion && item.match_status === 'unit_mismatch' && (
        <Small>
          Найдена позиция «{estimateLabel(suggestion)}» в «{suggestion.unit ?? '—'}», а у строки «
          {item.unit ?? '—'}»: объём в других измерителях не оценивается. Выберите другую позицию,
          отметьте «нет в смете» или исправьте единицу факта.
        </Small>
      )}

      <Field style={{ marginTop: 12 }}>
        Другая позиция сметы (номер, шифр или наименование)
        <Combobox
          id={`ks-estimate-${item.id}`}
          value={chosen?.id ?? ''}
          selectedLabel={chosen ? estimateLabel(chosen) : undefined}
          options={options}
          onChange={(o) =>
            setChosen(o ? (itemsQ.data?.items.find((it) => it.id === o.id) ?? null) : null)
          }
          onQueryChange={onQueryChange}
          placeholder="Начните вводить…"
          loading={itemsQ.isFetching}
          emptyText={
            itemsQ.isError
              ? apiErrorText(itemsQ.error, 'Позиции сметы не загрузились')
              : 'Позиция не найдена'
          }
        />
      </Field>
      {chosen && <Small>{estimateSublabel(chosen)}</Small>}

      <Check>
        <input
          type="checkbox"
          checked={scope === 'work'}
          disabled={!item.fact_id}
          onChange={(e) => setForWork(e.target.checked)}
        />
        Для всех строк этой работы в проекте (и будущих фактов)
      </Check>

      <Actions>
        <Button
          $size="sm"
          disabled={!chosen || busy}
          onClick={() => chosen && void decide(chosen.id, 'Позиция выбрана.')}
        >
          Выбрать эту позицию
        </Button>
        <Button
          $variant="secondary"
          $size="sm"
          disabled={busy}
          onClick={() => void decide(null, 'Отмечено: работы в смете нет.')}
        >
          Нет в смете
        </Button>
        {decided && (
          <Button $variant="ghost" $size="sm" disabled={busy} onClick={() => void reset()}>
            Снять решение
          </Button>
        )}
      </Actions>
      {error && <ErrorNote>{error}</ErrorNote>}
    </Panel>
  );
}

export function PtoKs() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');
  const { data: ptoInfo } = useGetPtoInfoQuery();
  const smetaKsPackageAvailable = ptoInfo?.flags?.smeta_ks_package ?? false;

  const {
    data: draftsData,
    error: listError,
    refetch,
  } = useListKsDraftsQuery(projectId, { skip: !projectId });
  const [generate, { isLoading: generating }] = useGenerateKsDraftMutation();
  const [exportXlsx, { isFetching: exporting }] = useLazyExportKsDraftXlsxQuery();
  const [pushToSmeta, { isLoading: pushingToSmeta }] = usePushKsDraftToSmetaMutation();
  const [createRequest, { isLoading: creatingRequest }] = useCreateRequestMutation();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [rowId, setRowId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data: detail } = useGetKsDraftQuery(selected ?? '', { skip: !selected });

  const disabled = isDisabled(listError);
  const drafts = draftsData?.drafts ?? [];
  const items = detail?.items ?? [];
  const summary = detail?.summary ?? localSummary(items);
  const suggestionById = new Map((detail?.suggestions ?? []).map((s) => [s.id, s]));
  // Работы, у которых есть строка «в пределах договора»: решение показываем на
  // ней, а не на строке «сверх договора» той же работы.
  const factWithin = new Set(
    items.filter((it) => !it.over_contract && it.fact_id).map((it) => it.fact_id as string),
  );
  const row = items.find((it) => it.id === rowId) ?? null;

  async function onGenerate() {
    setNotice(null);
    if (!from || !to) {
      setNotice('Укажите период (с и по).');
      return;
    }
    try {
      const res = await generate({ projectId, from, to }).unwrap();
      setSelected(res.draft.id);
      setRowId(null);
      setNotice(`Сформировано строк: ${res.items.length}.`);
    } catch (e) {
      // Тело ошибки — объект {code, message}: класть его в state как есть нельзя —
      // React падает на рендере объекта, и страница становится белой.
      setNotice(apiErrorText(e, 'Не удалось сформировать КС-2.'));
    }
  }

  async function onDownload(id: string) {
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ks2_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setNotice(await blobErrorText(e, 'Не удалось выгрузить XLSX.'));
    }
  }

  async function onPushToSmeta(draftId: string) {
    setNotice(null);
    try {
      const res = await pushToSmeta({ draftId }).unwrap();
      const queueTail = res.build_queued
        ? ''
        : ' Постановка в очередь не удалась — запустите сборку пакета вручную.';
      setNotice(
        `Черновик КС-2 передан в Пакет акта Сметчика (КС-3 №${res.package.ks3_no}, статус: ${res.package.status}).${queueTail}`,
      );
    } catch (e) {
      setNotice(apiErrorText(e, 'Не удалось передать черновик КС-2 в Сметчика.'));
    }
  }

  async function onRequestPriceRevision() {
    if (!selected) return;
    setNotice(null);
    try {
      const draftObj = drafts.find((d) => d.id === selected);
      const periodStr = draftObj ? `${draftObj.period_from} — ${draftObj.period_to}` : selected;
      await createRequest({
        request_type: 'contract_price_revision',
        source_function: 'pto',
        title: `Пересмотр цены договора (ДС): превышение объёма по КС-2 (${periodStr})`,
        priority: 'high',
        project_id: projectId,
        payload: {
          comment: `Выявлено превышение договорного объёма в черновике КС-2 (${periodStr}): строк сверх договора — ${summary.over_contract_lines}, на сумму ${rub(summary.over_contract_cost)}. Требуется подготовка дополнительного соглашения.`,
          over_contract_lines: summary.over_contract_lines,
          over_contract_cost: summary.over_contract_cost,
          ks_draft_id: selected,
        },
        submit: true,
      }).unwrap();
      setNotice('Заявка на подготовку ДС (пересмотр цены договора) направлена в Юрслужбу.');
    } catch (e) {
      setNotice(apiErrorText(e, 'Не удалось подать заявку в Юрслужбу.'));
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>КС-2 из факта</h1>
          <Muted>
            Черновик из принятых фактов объёма против договорной сметы. Кандидатов и неоднозначные
            строки подтверждает инженер; объём сверх договорного выделен и в итог не входит. Выпуск
            за инженером.
          </Muted>
        </div>
      </Row>

      {disabled && (
        <Banner>
          Функция КС-2 отключена на сервере. Включите <code>OSNOVA_FIELD_KS_ENABLED=1</code> в
          окружении backend.
        </Banner>
      )}
      {notice && <Banner>{notice}</Banner>}

      <Columns>
        <div>
          {/* Мост проект → договор. */}
          <ContractPanel
            projectId={projectId}
            canWrite={can_write}
            disabled={disabled}
            onNotice={setNotice}
          />

          {/* Генерация за период. */}
          <Panel>
            <h3 style={{ marginTop: 0 }}>Сформировать КС-2</h3>
            <Filters>
              <Field style={{ marginBottom: 0 }}>
                с
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Field>
              <Field style={{ marginBottom: 0 }}>
                по
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </Field>
            </Filters>
            <Button onClick={onGenerate} disabled={!can_write || disabled || generating}>
              {generating ? 'Формирую…' : 'Сформировать'}
            </Button>
          </Panel>

          {/* Список черновиков. */}
          <Panel>
            <Row style={{ marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Черновики</h3>
              <Button $variant="secondary" onClick={() => refetch()} disabled={disabled}>
                Обновить
              </Button>
            </Row>
            {drafts.length === 0 && <Muted>Пока нет.</Muted>}
            {drafts.map((d) => (
              <DraftItem
                key={d.id}
                $active={d.id === selected}
                onClick={() => {
                  setSelected(d.id);
                  setRowId(null);
                }}
              >
                {draftLabel(d)}
              </DraftItem>
            ))}
          </Panel>
        </div>

        {/* Строки выбранного черновика. */}
        <div>
          {!selected && <Muted>Выберите черновик слева или сформируйте новый.</Muted>}
          {selected && (
            <>
              <Row style={{ marginBottom: 8 }}>
                <Muted>
                  Строк: {summary.lines} · работ: {summary.works}
                  {summary.works > 0 && (
                    <>
                      {' '}
                      · в стоимости: {summary.priced} (сматчено {summary.unique}, подтверждено ПТО{' '}
                      {summary.confirmed}) · ждут решения: {summary.need_decision} (кандидатов{' '}
                      {summary.candidate}, единицы не совпадают {summary.unit_mismatch},
                      неоднозначно {summary.ambiguous}) · нет в смете:{' '}
                      {summary.no_match + summary.declined}
                    </>
                  )}
                </Muted>
                <Actions style={{ marginTop: 0 }}>
                  {can_write && smetaKsPackageAvailable && (
                    <Button
                      $variant="primary"
                      onClick={() => void onPushToSmeta(selected)}
                      disabled={pushingToSmeta}
                    >
                      {pushingToSmeta ? 'Передаю…' : 'Передать в Пакет акта Сметчика →'}
                    </Button>
                  )}
                  <Button
                    $variant="secondary"
                    onClick={() => onDownload(selected)}
                    disabled={exporting}
                  >
                    {exporting ? 'Готовлю…' : 'Скачать XLSX'}
                  </Button>
                </Actions>
              </Row>
              <Totals>
                <div>
                  Итого в пределах договора: <b>{rub(summary.total_cost)}</b>
                </div>
                {detail?.coverage?.summary && detail.coverage.summary.facts_total > 0 && (
                  <div>
                    Обеспеченность ИД (АОСР): с актом{' '}
                    <b>
                      {detail.coverage.summary.facts_with_aosr} из{' '}
                      {detail.coverage.summary.facts_total}
                    </b>{' '}
                    фактов (подписано / принято:{' '}
                    <b>{detail.coverage.summary.facts_with_signed_aosr}</b>)
                    {(detail.coverage.summary.rfi_total ?? 0) > 0 && (
                      <>
                        {' · '}RFI Заказчика: закрыто{' '}
                        <b>
                          {detail.coverage.summary.rfi_closed ?? 0} из{' '}
                          {detail.coverage.summary.rfi_total}
                        </b>
                      </>
                    )}
                    .
                  </div>
                )}
                {summary.over_contract_lines > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span>
                      <Over>Сверх договорного объёма:</Over> строк {summary.over_contract_lines} на{' '}
                      {rub(summary.over_contract_cost)} по договорной цене — в итог не входит (нужно
                      доп. соглашение).
                    </span>
                    {can_write && (
                      <Button
                        $variant="secondary"
                        $size="sm"
                        onClick={() => void onRequestPriceRevision()}
                        disabled={creatingRequest}
                      >
                        {creatingRequest ? 'Отправка…' : 'Запросить ДС в Юрслужбе'}
                      </Button>
                    )}
                  </div>
                )}
                {summary.need_decision > 0 && (
                  <div>
                    Строки «ждут решения» в стоимость не входят: нажмите «Решить…» в строке.
                  </div>
                )}
              </Totals>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Поз.</th>
                      <th>Шифр</th>
                      <th>Наименование</th>
                      <th>Ед.</th>
                      <th>Объём</th>
                      <th>Накопит.</th>
                      <th>Договор</th>
                      <th>Цена</th>
                      <th>Стоимость</th>
                      <th>Сопоставление</th>
                      <th>ИД / КФО (СК·ГК·RFI)</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const s = it.matched_estimate_item_id
                        ? suggestionById.get(it.matched_estimate_item_id)
                        : undefined;
                      const proposed =
                        s &&
                        (it.match_status === 'candidate' || it.match_status === 'unit_mismatch');
                      const showAction =
                        can_write &&
                        (!it.over_contract || !(it.fact_id && factWithin.has(it.fact_id)));
                      const factCov =
                        it.fact_id && detail?.coverage?.by_fact
                          ? detail.coverage.by_fact[it.fact_id]
                          : undefined;
                      const kfoCov =
                        (factCov?.position_id && detail?.coverage?.by_position
                          ? detail.coverage.by_position[factCov.position_id]
                          : undefined) ??
                        (it.code && detail?.coverage?.by_code
                          ? detail.coverage.by_code[it.code.toLowerCase()]
                          : undefined);
                      const rfiTotal = factCov?.rfi_total || kfoCov?.rfi_total || 0;
                      const rfiClosed = factCov?.rfi_closed || kfoCov?.rfi_closed || 0;
                      return (
                        <tr
                          key={it.id}
                          data-over={it.over_contract}
                          data-selected={it.id === rowId}
                        >
                          <td className="num">{it.position_no ?? ''}</td>
                          <td>{it.code ?? ''}</td>
                          <td>{it.name}</td>
                          <td>{it.unit ?? ''}</td>
                          <td className="num">{num(it.qty)}</td>
                          <td className="num">
                            {num(it.qty_cumulative)}
                            {it.over_contract ? ' ⚠' : ''}
                          </td>
                          <td className="num">{num(it.contract_qty)}</td>
                          <td className="num">{num(it.unit_price)}</td>
                          <td className="num">{num(it.cost)}</td>
                          <td className="wrap">
                            {MATCH_LABEL[it.match_status] ?? it.match_status}
                            {it.over_contract && (
                              <>
                                {' · '}
                                <Over>сверх договора</Over>
                              </>
                            )}
                            {proposed && s && <Small>предложено: {estimateLabel(s)}</Small>}
                          </td>
                          <td className="wrap">
                            {factCov ? (
                              factCov.has_aosr ? (
                                <span>
                                  АОСР {factCov.act_no ? `№ ${factCov.act_no} ` : ''}
                                  {factCov.aosr_signed ? '(подп.)' : `(${factCov.act_status ?? 'черн.'})`}
                                </span>
                              ) : (
                                <Over>нет АОСР</Over>
                              )
                            ) : (
                              '—'
                            )}
                            {rfiTotal > 0 && (
                              <Small>
                                RFI: закрыто {rfiClosed} из {rfiTotal}
                              </Small>
                            )}
                            {kfoCov && kfoCov.has_kfo && (
                              <Small>
                                СК: {num(kfoCov.accepted_sk)} · ГК: {num(kfoCov.accepted_gk)} · ИД:{' '}
                                {num(kfoCov.id_accepted)}
                              </Small>
                            )}
                          </td>
                          <td>
                            {showAction && (
                              <Button
                                $variant={NEEDS_DECISION.has(it.match_status) ? 'primary' : 'ghost'}
                                $size="sm"
                                onClick={() => setRowId(it.id === rowId ? null : it.id)}
                              >
                                {NEEDS_DECISION.has(it.match_status) ? 'Решить…' : 'Изменить…'}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
              {row && selected && (
                <DecisionPanel
                  key={row.id}
                  item={row}
                  draftId={selected}
                  suggestion={
                    row.matched_estimate_item_id
                      ? (suggestionById.get(row.matched_estimate_item_id) ?? null)
                      : null
                  }
                  onDone={(text) => {
                    setRowId(null);
                    setNotice(text);
                  }}
                  onClose={() => setRowId(null)}
                />
              )}
              {(detail?.by_position?.length ?? 0) > 0 && (
                <>
                  <h3>Сводка по позициям сметы</h3>
                  <Small>
                    Как в форме КС-2: одна позиция сметы — одна строка (только строки в стоимости).
                  </Small>
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <th>Поз.</th>
                          <th>Шифр</th>
                          <th>Наименование по смете</th>
                          <th>Ед.</th>
                          <th>За период</th>
                          <th>Сверх договора</th>
                          <th>Накопит.</th>
                          <th>Договор</th>
                          <th>Цена</th>
                          <th>Стоимость</th>
                          <th>КФО (СК / ГК / ИД / RFI)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail?.by_position ?? []).map((p) => {
                          const kfo =
                            p.code && detail?.coverage?.by_code
                              ? detail.coverage.by_code[p.code.toLowerCase()]
                              : undefined;
                          return (
                            <tr key={p.estimate_item_id} data-over={p.qty_over > 0}>
                              <td className="num">{p.position_no ?? ''}</td>
                              <td>{p.code ?? ''}</td>
                              <td className="wrap">{p.name ?? ''}</td>
                              <td>{p.unit ?? ''}</td>
                              <td className="num">{num(p.qty_within)}</td>
                              <td className="num">{p.qty_over > 0 ? num(p.qty_over) : ''}</td>
                              <td className="num">{num(p.qty_cumulative)}</td>
                              <td className="num">{num(p.contract_qty)}</td>
                              <td className="num">{num(p.unit_price)}</td>
                              <td className="num">{num(p.cost)}</td>
                              <td>
                                {kfo && kfo.has_kfo
                                  ? `СК ${num(kfo.accepted_sk)} · ГК ${num(kfo.accepted_gk)} · ИД ${num(kfo.id_accepted)}${(kfo.rfi_total ?? 0) > 0 ? ` · RFI ${kfo.rfi_closed ?? 0}/${kfo.rfi_total}` : ''}`
                                  : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </TableWrap>
                </>
              )}
            </>
          )}
        </div>
      </Columns>
    </div>
  );
}
